import { useQuery } from '@tanstack/react-query';
import { callRpc, listRows } from '@/api/resources';
import { signedUrlsFor } from '@/api/documents';
import { isSupabaseConfigured } from '@/lib/supabase';
import type { Letterhead } from '@/lib/consentDocument';

export interface LetterheadRow {
  organization_id: string; display_name: string | null; logo_document_id: string | null;
  address_lines: string | null; contact_line: string | null; registration_line: string | null;
  footer_text: string | null; accent_colour: string | null;
}

async function toDataUri(url: string): Promise<string | null> {
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

interface DocumentMark {
  name: string | null; brand_logo_path: string | null; contact_email: string | null; contact_phone: string | null; website: string | null;
  registered_address: string | null; office_name: string | null; office_address: string | null; office_phone: string | null;
}

/**
 * The organisation's letterhead for documents, with its logo already embedded as a data: URI so
 * that a generated form keeps it permanently (owner, 2026-09-21: "org based consent templates,
 * as it will have its own letter heads").
 *
 * Filled from the organisation's own mark where the letterhead is silent (owner, 2026-09-27: "add
 * the logos, project office addresses and so on for what we created in digital consents?"): the
 * brand logo, the registered address, the contacts, and - given the farmer's party - the office
 * in their state, as the emails already carry (api.fn_document_mark). The letterhead still wins
 * field by field; `row` is the letterhead as stored, for the screen that edits it.
 */
export function useLetterhead(partyId?: string | null) {
  return useQuery({
    queryKey: ['document_letterheads', partyId ?? null],
    enabled: isSupabaseConfigured,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<(Letterhead & { row: LetterheadRow | null }) | null> => {
      const [row, mark] = await Promise.all([
        listRows<LetterheadRow>('document_letterheads').then((rows) => rows[0] ?? null),
        callRpc<DocumentMark[]>('fn_document_mark', { p_party_id: partyId ?? null }).then((rows) => rows?.[0] ?? null).catch(() => null),
      ]);
      if (!row && !mark) return null;
      let logoDataUri: string | null = null;
      if (row?.logo_document_id) {
        const url = (await signedUrlsFor([row.logo_document_id]))[row.logo_document_id];
        if (url) logoDataUri = await toDataUri(url);
      }
      const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      if (!logoDataUri && mark?.brand_logo_path && base) {
        logoDataUri = await toDataUri(`${base}/storage/v1/object/public/organisation-logos/${mark.brand_logo_path}`);
      }
      const contacts = [mark?.contact_phone, mark?.contact_email, mark?.website].filter(Boolean).join(' · ');
      const office = mark?.office_address
        ? `Project office${mark.office_name ? ` (${mark.office_name})` : ''}: ${mark.office_address}${mark.office_phone ? ` · ${mark.office_phone}` : ''}`
        : null;
      return {
        display_name: row?.display_name || mark?.name || null,
        address_lines: row?.address_lines || mark?.registered_address || null,
        contact_line: row?.contact_line || contacts || null,
        registration_line: row?.registration_line ?? null,
        footer_text: row?.footer_text ?? null,
        accent_colour: row?.accent_colour ?? null,
        office_line: office,
        logoDataUri,
        row,
      };
    },
  });
}
